import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/Admin/AdminLayout';
import './AdminForm.css';

const AddCatalog = () => {
  const [form, setForm] = useState({ 
    title: 'Product Catalog',
    description: 'Complete collection of our premium products',
    version: '1.0',
    isActive: true
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingCatalogs, setExistingCatalogs] = useState([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCatalogs = async () => {
      try {
        setIsLoadingCatalogs(true);
        const response = await fetch('https://infuse-backend.onrender.com/api/catalog/all', {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch catalogs');
        }
        
        const data = await response.json();
        setExistingCatalogs(data);
      } catch (err) {
        console.error('Error fetching catalogs:', err);
        setError('Failed to load existing catalogs');
      } finally {
        setIsLoadingCatalogs(false);
      }
    };

    fetchCatalogs();
  }, []);

  const handleChange = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file && file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      return;
    }
    setError(null);
    setPdfFile(file);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!pdfFile) {
      setError('Please select a PDF file');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('version', form.version);
    formData.append('isActive', form.isActive);
    formData.append('catalogFile', pdfFile);

    try {
      const response = await fetch('https://infuse-backend.onrender.com/api/catalog', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload catalog');
      }

      // Navigate back to dashboard on success
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Error uploading catalog:', err);
      setError(err.message || 'Failed to upload catalog');
    } finally {
      setLoading(false);
    }
  };

  const activateCatalog = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(`https://infuse-backend.onrender.com/api/catalog/${id}/activate`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to activate catalog');
      }

      // Refresh catalog list
      const updatedCatalogs = existingCatalogs.map(catalog => ({
        ...catalog,
        isActive: catalog._id === id
      }));
      
      setExistingCatalogs(updatedCatalogs);
    } catch (err) {
      console.error('Error activating catalog:', err);
      setError('Failed to activate catalog');
    } finally {
      setLoading(false);
    }
  };

  const deleteCatalog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this catalog?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://infuse-backend.onrender.com/api/catalog/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete catalog');
      }

      setExistingCatalogs(existingCatalogs.filter(catalog => catalog._id !== id));
    } catch (err) {
      console.error('Error deleting catalog:', err);
      setError('Failed to delete catalog');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <AdminLayout>
      <div className="admin-form-container">
        <h2>Catalog Management</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <h3>Upload New Catalog</h3>
        <form className="admin-form" onSubmit={handleSubmit} encType="multipart/form-data">
          <label>Title:</label>
          <input 
            type="text" 
            name="title" 
            value={form.title} 
            onChange={handleChange} 
            required 
          />

          <label>Description:</label>
          <textarea
            name="description"
            rows="3"
            value={form.description}
            onChange={handleChange}
          />

          <label>Version:</label>
          <input
            type="text"
            name="version"
            value={form.version}
            onChange={handleChange}
            placeholder="1.0"
          />

          <label>PDF Catalog:</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            required
          />
          <div className="file-requirements">
            Max file size: 80MB. Only PDF files accepted.
          </div>

          <div className="form-checkbox">
            <input 
              type="checkbox" 
              id="isActive" 
              name="isActive" 
              checked={form.isActive}
              onChange={handleChange}
            />
            <label htmlFor="isActive">Set as active catalog (will replace current active catalog)</label>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Uploading...' : 'Upload Catalog'}
          </button>
        </form>

        <div className="existing-catalogs">
          <h3>Existing Catalogs</h3>
          
          {isLoadingCatalogs ? (
            <p>Loading catalogs...</p>
          ) : existingCatalogs.length > 0 ? (
            <div className="catalogs-grid">
              {existingCatalogs.map(catalog => (
                <div key={catalog._id} className={`catalog-item ${catalog.isActive ? 'active-catalog' : ''}`}>
                  <div className="catalog-content">
                    <h4>{catalog.title}</h4>
                    <p className="catalog-description">{catalog.description}</p>
                    <p className="catalog-meta">
                      {catalog.version && <span>Version: {catalog.version}</span>}
                      <span>Uploaded: {formatDate(catalog.uploadedAt)}</span>
                    </p>
                    {catalog.isActive && <div className="active-tag">Active</div>}
                  </div>
                  <div className="catalog-actions">
                    <a 
                      href={catalog.fileUrl} 
                      download={`${catalog.title.replace(/\s+/g, '_')}.pdf`}
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="download-btn"
                    >
                      <svg className="download-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download
                    </a>
                    {!catalog.isActive && (
                      <button 
                        onClick={() => activateCatalog(catalog._id)}
                        className="activate-btn"
                        disabled={loading}
                      >
                        Set Active
                      </button>
                    )}
                    <button 
                      onClick={() => deleteCatalog(catalog._id)}
                      className="delete-btn"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No catalogs available. Upload your first catalog above.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddCatalog;